/**
 * Backstory wire protocol v1.
 *
 * This file is the contract between the browser SDK, the Player, and the Go
 * services. The Go mirror lives in services/internal/protocol/protocol.go and
 * must stay field-for-field identical. Changes require bumping PROTOCOL_VERSION
 * and a note in docs/PROTOCOL.md.
 *
 * Encoding v1: JSON, deflate-compressed ("json+deflate"). Protobuf + zstd is
 * planned as encoding v2 (TASKS T0.2.3) and is selected per chunk by the
 * X-Backstory-Encoding header, so both can coexist.
 */
export const PROTOCOL_VERSION = 1;
export const LEVELS = ["HEALTHY", "CONSTRAINED", "DEGRADED", "SURVIVAL", "PAUSED"];
/** Hard input caps enforced by the gateway (PLAN §20.5). */
export const LIMITS = {
    maxCompressedChunkBytes: 1 * 1024 * 1024,
    maxDecompressedChunkBytes: 8 * 1024 * 1024,
    maxEventsPerChunk: 5000,
    maxConsoleArgBytes: 1024,
    maxTextNodeBytes: 64 * 1024,
};
// ---------------------------------------------------------------------------
// HTTP contract
// ---------------------------------------------------------------------------
export const HEADERS = {
    projectKey: "x-backstory-project-key",
    encoding: "x-backstory-encoding",
    sdkVersion: "x-backstory-sdk",
    /** Server time in epoch ms, returned on every response for clock sync. */
    serverTime: "x-backstory-server-time",
    /** Correlation id for one recording. A UUID. The SDK creates it when the page does not already have one. */
    requestId: "x-request-id",
};
export function validateChunk(input) {
    const errors = [];
    const c = input;
    if (!c || typeof c !== "object")
        return { ok: false, errors: ["chunk must be an object"] };
    if (c.v !== PROTOCOL_VERSION)
        errors.push(`v must be ${PROTOCOL_VERSION}`);
    for (const k of ["sessionId", "visitId"]) {
        if (typeof c[k] !== "string" || c[k].length < 8 || c[k].length > 64)
            errors.push(`${k} must be a string of 8..64 chars`);
    }
    for (const k of ["segmentIndex", "seq", "checkpointId", "firstTs", "lastTs", "droppedBytes"]) {
        if (typeof c[k] !== "number" || !Number.isFinite(c[k]) || c[k] < 0)
            errors.push(`${k} must be a non-negative number`);
    }
    if (typeof c.seq === "number" && c.seq < 1)
        errors.push("seq starts at 1");
    if (!LEVELS.includes(c.level))
        errors.push("level invalid");
    if (!Array.isArray(c.events))
        errors.push("events must be an array");
    else {
        if (c.events.length > LIMITS.maxEventsPerChunk)
            errors.push(`events exceeds ${LIMITS.maxEventsPerChunk}`);
        for (let i = 0; i < c.events.length; i++) {
            const e = c.events[i];
            if (!e || typeof e.type !== "string")
                errors.push(`events[${i}] missing type`);
            if (typeof e?.t !== "number")
                errors.push(`events[${i}] missing t`);
        }
    }
    if (typeof c.firstTs === "number" && typeof c.lastTs === "number" && c.lastTs < c.firstTs)
        errors.push("lastTs < firstTs");
    return { ok: errors.length === 0, errors };
}
/** Stable id generation: 26 chars, time-prefixed for sortable object keys. */
export function newId(now = Date.now()) {
    const alphabet = "0123456789abcdefghjkmnpqrstvwxyz"; // Crockford base32
    let ts = "";
    let n = now;
    for (let i = 0; i < 10; i++) {
        ts = alphabet[n % 32] + ts;
        n = Math.floor(n / 32);
    }
    let rand = "";
    const bytes = new Uint8Array(16);
    if (typeof crypto !== "undefined" && crypto.getRandomValues)
        crypto.getRandomValues(bytes);
    else
        for (let i = 0; i < 16; i++)
            bytes[i] = Math.floor(Math.random() * 256);
    for (let i = 0; i < 16; i++)
        rand += alphabet[bytes[i] % 32];
    return ts + rand;
}
/** Object key layout shared by gateway and worker. */
export function chunkObjectKey(tenantId, sessionId, seq) {
    return `${tenantId}/${sessionId}/${String(seq).padStart(8, "0")}.bin`;
}
export * from "./api.js";
export * from "./api-auth.js";
export * from "./config.js";
export * from "./signals.js";
export * from "./api-insights.js";
export * from "./transcript.js";
export * from "./api-ai.js";
export * from "./live.js";
export * from "./api-billing.js";
export * from "./api-staff.js";
export * from "./api-help.js";
export * from "./webrtc.js";
export * from "./api-calls.js";
//# sourceMappingURL=index.js.map